import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import './VisaStatsEmbed.css';

const metricConfig = {
  'approval-rate': {
    label: 'Current Approval Rate',
    format: (value) => (value != null ? `${(value * 100).toFixed(1)}%` : '—'),
    helper: 'Share of applications approved in the selected period.'
  },
  'processing-time': {
    label: 'Average Processing Time',
    format: (value) => (value ? `${value} days` : '—'),
    helper: 'Median days between submission and decision.'
  },
  'daily-decisions': {
    label: 'Decisions Published Today',
    format: (value) => (value != null ? value.toLocaleString() : '—'),
    helper: 'Combined approvals + refusals.'
  }
};

const periodLabels = {
  today: 'Today',
  week: 'Last 7 days',
  month: 'Last 30 days',
  year: 'Past year'
};

const VisaStatsEmbed = ({ type = 'approval-rate', period = 'month' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/api/visa-stats`);
        if (!response.ok) throw new Error('Unable to load visa stats');
        const data = await response.json();
        if (!cancelled) {
          setStats(data || {});
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const config = metricConfig[type] || metricConfig['approval-rate'];

  const getValue = () => {
    if (!stats) return null;
    switch (type) {
      case 'processing-time':
        return stats?.processingTime?.[period] ?? stats?.processingTime?.overall ?? null;
      case 'daily-decisions':
        return stats?.decisions?.[period] ?? stats?.decisions?.today ?? null;
      case 'approval-rate':
      default:
        return stats?.approvalRate?.[period] ?? stats?.approvalRate?.overall ?? null;
    }
  };

  const value = getValue();
  const periodLabel = periodLabels[period] || 'Latest';

  return (
    <div className="visa-stats-embed">
      <div className="visa-stats-embed__header">
        <span className="visa-stats-embed__label">{config.label}</span>
        <span className="visa-stats-embed__period">{periodLabel}</span>
      </div>
      <div className="visa-stats-embed__value">
        {loading && <span className="muted">Loading…</span>}
        {!loading && error && <span className="muted">Data unavailable</span>}
        {!loading && !error && config.format(value)}
      </div>
      <p className="visa-stats-embed__helper">{config.helper}</p>
    </div>
  );
};

export default VisaStatsEmbed;
